import { type PropsWithChildren, type RefObject, memo } from 'react';
import { Popover } from '@nexelem/components';
import { useGanttDraggable, useOnNodeClick } from '../../hooks';
import { NodeType, type QueueTaskNode } from '../../types';
import { getPossibleQueues, useIsGanttNodeDraggable } from '../../utils';
import { useFormatTaskDuration } from '../../hooks/useFormatTaskDuration';
import { useProductionPlanQueryParams } from '../../../hooks';
import { isSearchEmphasizedSelector, isSearchSelectedSelector, useProductionPlanSearchStore } from '../../../store';
import { NodeSingleBase } from './NodeSingleBase';
import { GanttUtils } from '../../../utils';
import { useIsOpenTaskDetailsDrawer } from '../../../../../../pages/generic/Products/components/TaskDetailDrawer/TaskDetailsDrawer';
import { GanttConfig } from '../../GanttConfig';
import { TaskMainValueOptions, useProductionPlanSettings } from '../../../components';
import { useNodeShadowParams } from './useNodeShadowParams';
import { useNodeFlagsMapper } from '../../mappers';
import { NodeSinglePopoverContent, useCursorHoverState } from '../NodePopover';

export type SingleNodeData = {
  taskId?: number | string;
  taskExecutionId?: number;
};
type SingleNodeGanttProps = {
  node: QueueTaskNode;
};

export const NodeSingle = memo(({ node, children }: PropsWithChildren<SingleNodeGanttProps>) => {
  const { queryParams } = useProductionPlanQueryParams();
  const formatTaskDuration = useFormatTaskDuration({ compact: true });

  const nodeFlags = useNodeFlagsMapper(node);

  const { onNodeClick } = useOnNodeClick({ node, nodeFlags });

  const isSearchEmphasized = useProductionPlanSearchStore(isSearchEmphasizedSelector([node.id]));
  const isSearchSelected = useProductionPlanSearchStore(isSearchSelectedSelector([node.id]));

  const isNodeDraggable = useIsGanttNodeDraggable(node);

  const shadowParams = useNodeShadowParams(node);

  const { setNodeRef, attributes, listeners, isDragging } = useGanttDraggable({
    id: node.id,
    disabled: !isNodeDraggable,
    data: { id: Number(node.id), possibleQueues: getPossibleQueues(node), type: NodeType.TASK },
  });

  const taskMainValue = useProductionPlanSettings((state) => state.taskMainValue);
  const showOnTask = useProductionPlanSettings((state) => state.gantt.showOnTask);

  const isOpen = useIsOpenTaskDetailsDrawer();

  const { retoolingWidth } = GanttUtils.calculateRetooling(node);
  const isSmall = node.nodeWidth <= GanttConfig.SMALL_NODE_WIDTH;

  const isTaskSelected = isOpen && queryParams.taskId === node.id;

  const tooltip = useCursorHoverState(isDragging);
  const title = taskMainValue === TaskMainValueOptions.PRODUCT ? node.productName : node.name;

  return (
    <Popover.Root open={tooltip.open} onOpenChange={tooltip.onOpenChange}>
      <Popover.Anchor virtualRef={tooltip.virtualRef} />
      <NodeSingleBase
        attributes={attributes}
        clients={GanttUtils.getClients(node.orders)}
        current={node.progress.current}
        date={formatTaskDuration(node)}
        dueDate={node.dueDate}
        hasError={GanttUtils.hasCriticalError(node.errors)}
        id={node.id}
        isDraggable={isNodeDraggable}
        isDragging={isDragging}
        isEmphasized={isSearchEmphasized}
        isSearchSelected={isSearchSelected}
        isSelected={isTaskSelected}
        isSmall={isSmall}
        listeners={listeners}
        mode={queryParams.mode}
        nodeFlags={nodeFlags}
        nodeIds={[node.id]}
        productionOrderExternalNumber={node.productionOrderDetails?.externalId}
        productionOrderNumber={node.productionOrderDetails?.number}
        ref={setNodeRef as unknown as RefObject<HTMLDivElement>}
        requiredSoftResourcesAmount={node.requiredSoftResourcesAmount}
        retoolingWidth={retoolingWidth}
        salesOrderIds={GanttUtils.getSalesOrderIds(node.orders)}
        shadowParams={shadowParams}
        showOnTask={showOnTask}
        softResourceAssignments={node.softResourceAssignments}
        status={node.status}
        statusColor={GanttUtils.getTaskStatusColor(node.status, node.errors)}
        title={title}
        total={node.progress.totalAmount.amount}
        unit={node.progress.totalAmount.unit.name}
        width={node.nodeWidth}
        onClick={onNodeClick}
        onMouseEnter={tooltip.onMouseEnter}
        onMouseLeave={tooltip.onMouseLeave}
        onMouseMove={tooltip.onMouseMove}
      >
        {children}
      </NodeSingleBase>
      <Popover.Content side="bottom" sideOffset={16} onOpenAutoFocus={(event) => event.preventDefault()}>
        <NodeSinglePopoverContent
          dueDate={node.dueDate}
          endDate={node.endDate}
          externalId={node.productionOrderDetails?.externalId}
          productionOrderNumber={node.productionOrderDetails?.number}
          showOnTask={showOnTask}
          startDate={node.startDate}
          status={node.status}
          title={title}
          total={node.progress.totalAmount.amount}
          unit={node.progress.totalAmount.unit.name}
        />
      </Popover.Content>
    </Popover.Root>
  );
});

NodeSingle.displayName = 'NodeSingle';
